import { useState, useRef, useCallback, useEffect } from 'react';
import type { Coordinates } from '../types';

export type NordicCountry = 'sweden' | 'denmark' | 'finland' | 'iceland';

const NORDIC_BOUNDS: Record<NordicCountry, { latMin: number; latMax: number; lonMin: number; lonMax: number }> = {
  sweden:  { latMin: 55.3, latMax: 69.1, lonMin: 11.1, lonMax: 24.2 },
  denmark: { latMin: 54.5, latMax: 57.8, lonMin: 8.0,  lonMax: 15.2 },
  finland: { latMin: 59.8, latMax: 70.1, lonMin: 20.5, lonMax: 31.6 },
  iceland: { latMin: 63.3, latMax: 66.6, lonMin: -24.5, lonMax: -13.5 },
};

const NORWAY_BOUNDS = { latMin: 57.9, latMax: 71.2, lonMin: 4.5, lonMax: 31.1 };

const AUTO_DISMISS_MS = 5000;
const COOLDOWN_MS = 300_000; // 5 min per country

function isInside(lat: number, lon: number, b: typeof NORWAY_BOUNDS): boolean {
  return lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax;
}

export function detectNordicCountry(coords: Coordinates): NordicCountry | null {
  const { latitude: lat, longitude: lon } = coords;
  if (isInside(lat, lon, NORWAY_BOUNDS)) return null;
  for (const [country, bounds] of Object.entries(NORDIC_BOUNDS) as [NordicCountry, typeof NORWAY_BOUNDS][]) {
    if (isInside(lat, lon, bounds)) return country;
  }
  return null;
}

export function useNordicComingSoon(enabledCountries?: Partial<Record<NordicCountry, boolean>>) {
  const [activeCountry, setActiveCountry] = useState<NordicCountry | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldowns = useRef<Map<string, number>>(new Map());

  const checkCoords = useCallback((coords: Coordinates) => {
    const country = detectNordicCountry(coords);

    if (!country || enabledCountries?.[country]) {
      if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
      setActiveCountry(null);
      return;
    }

    const last = cooldowns.current.get(country) ?? 0;
    if (Date.now() - last < COOLDOWN_MS) return;

    setActiveCountry(prev => {
      if (prev === country) return prev;
      cooldowns.current.set(country, Date.now());
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setActiveCountry(null), AUTO_DISMISS_MS);
      return country;
    });
  }, [enabledCountries]);

  const dismiss = useCallback(() => {
    if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
    setActiveCountry(null);
  }, []);

  useEffect(() => () => { if (dismissTimer.current) clearTimeout(dismissTimer.current); }, []);

  return { activeCountry, checkCoords, dismiss };
}
